import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, DatePicker, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { ContractorDto, UpdateContractorCommand, CreateContractorCommand } from 'api/models';
import { ContractorsApi } from 'api/api';
import openNotificationWithIcon from 'utility/notification';
import FeatherIcon from 'feather-icons-react';
import { BasicFormWrapper, CustomSwitch } from 'container/styled';

interface Props {
  visible: boolean;
  editMode: boolean;
  contractor?: ContractorDto | null;
  onSuccess: (values: CreateContractorCommand | UpdateContractorCommand) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const contractorsApi = new ContractorsApi();
export const ContractorForm: React.FC<Props> = ({ visible, editMode, contractor, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (contractor && editMode) {
      form.setFieldsValue({
        id: contractor.id,
        name: contractor.name,
        description: contractor.description,
        phoneNumber: contractor.phoneNumber,
      });
    }
  }, [contractor, editMode, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      if (editMode) {
        await contractorsApi.apiContractorsIdPut({
          id: values.id,
          updateContractorCommand: values,
        });
      } else {
        await contractorsApi.apiContractorsPost({
          createContractorCommand: values,
        });
      }

      openNotificationWithIcon(
        'success',
        t(
          `contractors:${editMode ? 'updated' : 'created'}`,
          `Contractor ${editMode ? 'updated' : 'created'} successfully`,
        ),
      );
      await onSuccess(
        editMode
          ? {
              ...values,
              id: contractor?.id,
            }
          : values,
      );
    } catch (err) {
      openNotificationWithIcon('error', t('contractors:error', 'Error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t(`contractors:${editMode ? 'edit' : 'create'}`, `${editMode ? 'Edit' : 'Create'} Contractor`)}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
    >
      <BasicFormWrapper>
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isActive: true }}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            label={t('contractors:name', 'Contractor Name')}
            name="name"
            rules={[
              { required: true, message: t('contractors:name-required', 'Contractor name is required') },
              { max: 200, message: t('contractors:name-max', 'Contractor name cannot exceed 200 characters') },
            ]}
          >
            <Input prefix={<FeatherIcon icon="briefcase" size={16} />} />
          </Form.Item>

          <Form.Item
            label={t('global:phone', 'Phone Number')}
            name="phoneNumber"
            rules={[
              {
                pattern: /^\+?[\d\s-]+$/,
                message: t('global:phone-invalid', 'Invalid phone number format'),
              },
            ]}
          >
            <Input prefix={<FeatherIcon icon="phone" size={16} />} />
          </Form.Item>

          <Form.Item
            label={t('contractors:description', 'Description')}
            name="description"
            rules={[
              { max: 500, message: t('contractors:description-max', 'Description cannot exceed 500 characters') },
            ]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          {editMode && (
            <Form.Item label={t('contractors:status', 'Status')} name="isActive" valuePropName="checked">
              <CustomSwitch />
            </Form.Item>
          )}

          <div className="form-actions">
            <Button onClick={onCancel} style={{ marginRight: 8 }}>
              {t('global:cancel', 'Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editMode ? t('global:update', 'Update') : t('global:create', 'Create')}
            </Button>
          </div>
        </Form>
      </BasicFormWrapper>
    </Modal>
  );
};
